import { useEffect, useRef, useState } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Currency } from '../../../common/currency';
import { hideLoader, showLoader } from '../../../store/common/slice';
import { getUserProfile } from '../../../store/auth/actions';
import { getBuildingTitle, getBuildingTitleText } from '../../../store/buildings/actions';
import BllinkUtils from '../../../common/utils/utils';
import {
    calcMonthsTextForSelectBox,
    createPdfFromHtml,
    shortenText
} from '../../../common/shared/statelessMethods';
import { StyledLetterNew } from './debtList.styled';
import {useManagementCompanyLogo} from "../../../common/hooks/useManagementCompanyLogo";

const WarningLetter = ({ row, clearSelectedRow, currency, getWarningLetter, downloadLetter, setDownload }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const letterContainer = useRef();
    const [address, setAddress] = useState('');
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const currentBuildingId = useSelector(state => state.buildings.currentBuildingData?.id);
    const userProfile = useSelector(state => state.auth.userProfile);
    const titleData = useSelector(state => state.buildings.titleData);
    const mcLogo = useManagementCompanyLogo(currentBuildingId, userProfile?.partnerID);

    useEffect(() => {
        if (mcLogo != null && mcLogo.noImage) {
            setIsImageLoaded(true);
        }
    }, [mcLogo]);

    useEffect(async () => {
        const buildingID = row.buildingID || row.buildingsData.buildingInternal;
        if (!titleData.city) {
            await dispatch(getBuildingTitle({buildingID}));
        }
        if (!userProfile.userFirstName) {
            await dispatch(getUserProfile());
        }

        const { payload } = await dispatch(getBuildingTitleText({t, buildingID}));
        if (payload) {
            const address = `${payload}, ${t('notifications.apartment')}: ${row.apartmentNumber}`;
            setAddress(address);
        } else setAddress('test')
    }, [row]);

    useEffect(async () => {
        dispatch(showLoader());

        const fileName = `${t('letter.fileName')}_${address}_${row.apartmentNumber}`;
        if (downloadLetter && isImageLoaded) {
            await createPdfFromHtml(letterContainer.current, fileName);
            if (!mcLogo?.noImage) {
                URL.revokeObjectURL(mcLogo.objectUrl);
            }
            clearSelectedRow();
            setDownload(false);
        }

        dispatch(hideLoader());
    }, [userProfile, isImageLoaded]);

    useEffect(() => {
        if (getWarningLetter) getWarningLetter(letterContainer.current, address)
    }, [address]);

    const { openMonthDebtPayments, openOneTimePayments } = row;
    const monthText = shortenText(calcMonthsTextForSelectBox(openMonthDebtPayments));

    const oneTimeText = openOneTimePayments.length ? openOneTimePayments.reduce((acc, curVal, index) => `${acc} ${curVal.payment_description}${index < openOneTimePayments.length - 1 ? ',' : ''}`, '') : '';

    const renderName = () => {
        const { RenterName, OwnerName } = row?.tenantDetails;
        if (RenterName && OwnerName) return `${RenterName} | ${OwnerName}` 
        else if (RenterName && !OwnerName) return RenterName
        else if (!RenterName && OwnerName) return OwnerName
        else return ''
    };

    const parsedDate = BllinkUtils.BllinkTime.parseDate(new Date());

    return (
        <StyledLetterNew className="warning-letter">
            <div ref={letterContainer} className='letter-container'>
                <div className='header'>
                    <div className='header-text'>
                        <div className='title'>{t('letter.new.title')}</div>
                        <div>03-5090045</div>
                        <a href="mailto:support@bllink.co">support@bllink.co</a>
                    </div>
                    <div className={`logo ${mcLogo?.noImage ? 'empty': ''}`}>
                        <img src={mcLogo?.objectUrl} alt="logo" onLoad={() => setIsImageLoaded(true)} />
                    </div>
                </div>
                <div className='user-info'>
                    <div>{`${t('letter.new.date')}: ${parsedDate}`}</div>
                    <div>{`${t('letter.new.to')}:`} {renderName()}</div>
                    <div>{`${t('letter.new.address')}: ${address}`}</div>
                </div>
                <div className='notice bold'>{t('letter.new.noticeBeforeLegalAction')}</div>
                <div className='list'>
                    <div>1. {t('letter.new.listItems.first')}</div>
                    <div>2. {t('letter.new.listItems.second')}</div>
                    <div>
                        <div className='table-wrapper bold'>
                            <table>
                                <thead>
                                    <tr>
                                        <td>{t('letter.new.table.payment')}</td>
                                        <td>{t('letter.new.table.monthInfo')}</td>
                                        <td>{t('letter.new.table.sum')}</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{t('letter.new.table.firstRow')}</td>
                                        <td>{monthText.originalComment || monthText || '-'}</td>
                                        {/* moving currency before amount is a temporary measure. Need to find another solution when localization is ready */}
                                        {row?.pastPaymentsLeft > 0 && <td><Currency>{currency}</Currency>{row.pastPaymentsLeft}</td>}
                                        {row?.pastPaymentsLeft === 0 && <td>-</td>}
                                    </tr>
                                    <tr>
                                        <td>{t('letter.new.table.secondRow')}</td>
                                        <td>{oneTimeText || '-'}</td>
                                        {row?.oneTimeLeft > 0 && <td><Currency>{currency}</Currency>{row.oneTimeLeft}</td>}
                                        {row?.oneTimeLeft === 0 && <td>-</td>}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className='total bold'>{`${t('letter.new.total')}:`} <Currency>{currency}</Currency>{`${row.pastPaymentsLeft + row.oneTimeLeft}`}</div>
                    </div>
                    <div>3. {t('letter.new.listItems.third')}</div>
                    <div>4. {t('letter.new.listItems.forth')}</div>
                    <div>5. {t('letter.new.listItems.fifth')}</div>
                    <div>6. {t('letter.new.listItems.sixth')}<span className='bold'> {t('letter.new.listItems.sixthHighlited')}</span></div>
                    <div className='highlitedItem bold'>7. {t('letter.new.listItems.seventh')}</div>
                    <div className='margin-to-break-pages'>8. {t('letter.new.listItems.eighth')}</div>
                    <div>9. {t('letter.new.listItems.ninth')}</div>
                    <div>10. {t('letter.new.listItems.tenth')}</div>
                    <div className='subList'>
                        11. {t('letter.new.listItems.eleventh')}
                        <div className='subItem'>• {t('letter.new.listItems.eleventhFirst')}</div>
                        <div className='subItem'>• {t('letter.new.listItems.eleventhTwo')}</div>
                    </div>
                </div>
                <div className='extra-info'>{t('letter.new.extraInfo')}</div>
            </div>
        </StyledLetterNew>
    );
};

export default WarningLetter;
